import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EventFormDialog({ open, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState<'microsoft' | 'google'>('microsoft');
  const [title, setTitle] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState('');
  const [description, setDescription] = useState('');
  const [onlineMeeting, setOnlineMeeting] = useState(false);

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke('calendar-events', {
        body: {
          provider,
          title,
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
          location: location || undefined,
          attendees: attendees
            ? attendees.split(',').map((e) => e.trim())
            : [],
          description: description || undefined,
          online_meeting: onlineMeeting,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      onOpenChange(false);
      setTitle('');
      setStart('');
      setEnd('');
      setLocation('');
      setAttendees('');
      setDescription('');
      setOnlineMeeting(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Event</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              onClick={() => setProvider('microsoft')}
              className={`rounded-md px-3 py-1 text-xs ${
                provider === 'microsoft'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              Outlook
            </button>
            <button
              onClick={() => setProvider('google')}
              className={`rounded-md px-3 py-1 text-xs ${
                provider === 'google'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              Google Calendar
            </button>
          </div>

          <div>
            <Label htmlFor="title">Title</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start">Start</Label>
              <Input id="start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="end">End</Label>
              <Input id="end" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>

          <div>
            <Label htmlFor="location">Location</Label>
            <Input id="location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Optional" />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="online"
              checked={onlineMeeting}
              onCheckedChange={(checked) => setOnlineMeeting(checked === true)}
            />
            <Label htmlFor="online" className="font-normal">
              Create online meeting ({provider === 'microsoft' ? 'Teams' : 'Google Meet'})
            </Label>
          </div>

          <div>
            <Label htmlFor="attendees">Attendees</Label>
            <Input
              id="attendees"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="email1@example.com, email2@example.com"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !title || !start || !end}>
              {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
